import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { otherOrderService } from "services";
import {
  OtherOrderForm,
  OtherOrderQuery,
  OtherOrderUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type OtherOrderListKey = ["other-orders"] | ["other-orders", OtherOrderQuery];
type OtherOrderDetailKey = ["other-orders", "detail", number | string];

const otherOrderKeys = {
  all: ["other-orders"] as const,
  list: (params?: OtherOrderQuery): OtherOrderListKey =>
    params ? ["other-orders", params] : ["other-orders"],
  detail: (id: number | string): OtherOrderDetailKey => [
    "other-orders",
    "detail",
    id,
  ],
};

export function useOtherOrder() {
  const queryClient = useQueryClient();

  const ensureSuccess = (res: any, defaultMessage: string) => {
    if (res && (res.statusCode === 200 || res.statusCode === 201)) {
      return res;
    }
    throw new Error(res?.message || defaultMessage || "Có lỗi xảy ra");
  };

  const fetchList = (params?: OtherOrderQuery, enabled = true) => {
    return useQuery({
      queryKey: otherOrderKeys.list(params),
      queryFn: () => otherOrderService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: otherOrderKeys.detail(id),
      queryFn: () => otherOrderService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createOtherOrder = useMutation({
    mutationFn: async (payload: OtherOrderForm) => {
      const res = await otherOrderService.store(payload);
      return ensureSuccess(res, "Tạo chứng từ khác thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Tạo chứng từ khác thành công");
      queryClient.invalidateQueries({ queryKey: otherOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error((err as any)?.message ?? "Tạo chứng từ khác thất bại");
      console.error("createOtherOrder failed", err);
    },
  });

  const updateOtherOrder = useMutation({
    mutationFn: async (payload: OtherOrderUpdatePayload) => {
      const res = await otherOrderService.update(payload);
      return ensureSuccess(res, "Cập nhật chứng từ khác thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Cập nhật chứng từ khác thành công");
      queryClient.invalidateQueries({ queryKey: otherOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error(
        (err as any)?.message ?? "Cập nhật chứng từ khác thất bại",
      );
      console.error("updateOtherOrder failed", err);
    },
  });

  const deleteOtherOrder = useMutation({
    mutationFn: async (id: number | string) => {
      const res = await otherOrderService.destroy(id);
      return ensureSuccess(res, "Xoá chứng từ khác thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Xoá chứng từ khác thành công");
      queryClient.invalidateQueries({ queryKey: otherOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error((err as any)?.message ?? "Xoá chứng từ khác thất bại");
      console.error("deleteOtherOrder failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createOtherOrder,
    updateOtherOrder,
    deleteOtherOrder,
  };
}
